import { useState } from "react";
import {
  Box,
  Card,
  CardActionArea,
  Collapse,
  Divider,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import type { OrderRelatedDataDto } from "@lib/ShiOneClient";
import type { OrderDrawerIdentifier } from "../types";
import { useOrderAdditionalDetails } from "../useOrderAdditionalDetails";

interface OrderDrawerAdditionalDetailsProps {
  identifier: OrderDrawerIdentifier;
  enabled: boolean;
  disableGutters?: boolean;
}

function DetailRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        py: 1,
      }}
    >
      <Typography variant="body1" color="text.secondary" sx={{ mr: 2 }}>
        {label}
      </Typography>

      <Typography
        variant="body1"
        fontWeight={500}
        sx={{ textAlign: "right", wordBreak: "break-word" }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function AdditionalDetailsContent({
  details,
}: {
  details: OrderRelatedDataDto[];
}) {
  return (
    <Box sx={{ px: 2 }}>
      {details.map((detail, index) => (
        <Box key={`${detail.fieldName}-${index}`}>
          <DetailRow
            label={detail.fieldName ?? ""}
            value={detail.valueString ?? "—"}
          />
          {index < details.length - 1 && (
            <Divider sx={{ borderColor: "divider" }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

export function OrderDrawerAdditionalDetails({
  identifier,
  enabled,
  disableGutters = false,
}: Readonly<OrderDrawerAdditionalDetailsProps>) {
  const [expanded, setExpanded] = useState(true);
  const { additionalDetails } = useOrderAdditionalDetails({
    identifier,
    enabled,
  });

  if (additionalDetails.length === 0) return null;

  return (
    <Card elevation={1} sx={{ mx: disableGutters ? 0 : 2, my: 0, borderRadius: 1 }}>
      <CardActionArea
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="subtitle1" sx={{ textTransform: "uppercase" }}>
          Additional Details
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
            color: "action.active",
          }}
        />
      </CardActionArea>
      <Collapse in={expanded}>
        <AdditionalDetailsContent details={additionalDetails} />
      </Collapse>
    </Card>
  );
}
